/**
 * Редактори пропорційного ПДВ (ст. 199 ПКУ) — етап 1 добудови
 * (рішення власника 2026-08-09): раніше сторінки були лише списками без
 * можливості створити документ.
 *
 * 1. PropCoefEditor — коефіцієнт (частка використання, ЧВ) на рік.
 * 2. PropRecalcEditor — річний перерахунок (факт vs використаний) +
 *    кнопка «розрахувати з продажів року» (/calculate-from-year).
 * 3. EVCVAEditor — коригування очікуваного/підтвердженого ПДВ (4 типи).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { evcva, type AdjustmentType } from '@/api/vatAdjustments';
import { taxInvoicesIncoming } from '@/api/taxInvoices';
import SearchSelect from '@/components/SearchSelect';
import { propCoef, propRecalc, type PropRecalcCalculateResponse } from '@/api/vatDocuments';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';

function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
import { useConfirm } from '@/components/ConfirmProvider';
import { pickDefaultOrg } from '@/store/defaults';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function nowLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: unknown } } }).response;
    const d = r?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => (x as { msg?: string }).msg ?? '').join('; ');
  }
  return String(e);
}

function OrgSelect({ value, onChange, disabled }: {
  value: string; onChange: (v: string) => void; disabled: boolean;
}) {
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  useEffect(() => {
    if (!value && orgsQ.data?.items.length) {
      const def = pickDefaultOrg(orgsQ.data.items);
      if (def) onChange(def.id);
    }
  }, [value, orgsQ.data, onChange]);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">—</option>
      {(orgsQ.data?.items ?? []).map((o) => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  );
}

// ============================ Коефіцієнт (ЧВ) ============================

export function PropCoefEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);
  const y = new Date().getFullYear();
  const [form, setForm] = useState({
    organization_id: '', document_number: '', document_date: nowLocal(),
    year: y, coefficient: '', based_on_year: y - 1,
    approval_date: `${y}-01-15`, comment: '',
  });

  const docQ = useQuery({
    queryKey: ['prop-coef-doc', id],
    queryFn: () => propCoef.get(id!),
    enabled: !isNew,
  });
  useEffect(() => {
    const d = docQ.data;
    if (!d) return;
    setForm({
      organization_id: d.organization_id,
      document_number: d.document_number,
      document_date: d.document_date.slice(0, 16),
      year: d.year, coefficient: d.coefficient,
      based_on_year: d.based_on_year,
      approval_date: d.approval_date ?? '',
      comment: d.comment ?? '',
    });
  }, [docQ.data]);
  const isPosted = !isNew && (docQ.data?.posted ?? false);

  const payload = () => ({
    organization_id: form.organization_id,
    document_number: form.document_number || null,
    document_date: form.document_date,
    year: Number(form.year),
    coefficient: form.coefficient,
    based_on_year: Number(form.based_on_year),
    approval_date: form.approval_date || null,
    comment: form.comment || null,
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['prop-coef'] });
    qc.invalidateQueries({ queryKey: ['prop-coef-doc', id] });
  };
  const create = useMutation({
    mutationFn: () => propCoef.create(payload()),
    onSuccess: (d) => { invalidate(); navigate(`/taxation/proportional-vat-coefficient/${d.id}`); },
    onError: (e) => setError(extractError(e)),
  });
  const updPayload = () => ({
    document_date: form.document_date,
    year: Number(form.year),
    coefficient: form.coefficient,
    based_on_year: Number(form.based_on_year),
    approval_date: form.approval_date || null,
    comment: form.comment || null,
  });
  const update = useMutation({
    mutationFn: () => propCoef.update(id!, updPayload()),
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    mutationFn: async () => {
      if (isNew) { const d = await create.mutateAsync(); return propCoef.post(d.id); }
      await propCoef.update(id!, updPayload());
      return propCoef.post(id!);
    },
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => propCoef.unpost(id!),
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => propCoef.remove(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['prop-coef'] }); navigate('/taxation/proportional-vat-coefficient'); },
    onError: (e) => setError(extractError(e)),
  });
  // Етап 2а: попап зведеної ПН (місяць/база придбань подвійного призначення).
  const [pnOpen, setPnOpen] = useState(false);
  const now = new Date();
  const [pnMonth, setPnMonth] = useState(now.getMonth() + 1);
  const [pnYear, setPnYear] = useState(now.getFullYear());
  const [pnBase, setPnBase] = useState('');
  const [pnReason, setPnReason] = useState('09');
  const [pnBusy, setPnBusy] = useState(false);
  const [pnResult, setPnResult] = useState<string | null>(null);

  // ч.889 (новий дизайн, Закон #10): «створити зведену ПН (ст. 199.1)» —
  // з шапки у «Повʼязані дії» (disabled до проведення, Закон #6).
  const railActions = [{
    // ч.1347: зведена ПН — зі збереженого коефіцієнта, проведення не чекаємо.
    label: 'створити зведену ПН (ст. 199.1)',
    title: isNew ? 'Спершу збережіть документ'
      : 'Щомісячна зведена ПН за ст. 199.1: ПЗ на необлагувану частку придбань подвійного призначення. Далі — підпис і реєстрація в ЄРПН зі сторінки ПН/РК (вихідні).',
    disabled: isNew,
    onClick: () => { setPnResult(null); setPnOpen(true); },
  }];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'coef', title: 'Коефіцієнт', required: 3,
      filled: (form.year ? 1 : 0) + (form.coefficient ? 1 : 0) + (form.based_on_year ? 1 : 0) },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          Коефіцієнт пропорційного ПДВ (ЧВ) {!isNew && `№ ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/taxation/proportional-vat-coefficient')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          savePending={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => post.mutate()}
          postPending={post.isPending}
          postTitle="Зберігає та проводить"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={async () => {
            if (await confirm('Видалити документ?')) remove.mutate();
          }}
          entriesDoc={{ type: 'doc.proportional_vat_coefficient', id: isNew ? '' : id! }}
        />
        </div>
      </div>
      {pnResult && (
        <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.85rem' }}>{pnResult}</div>
      )}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.proportional_vat_coefficient', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <OrgSelect value={form.organization_id}
            onChange={(v) => setForm((f) => ({ ...f, organization_id: v }))}
            disabled={!isNew} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа</label>
          <input value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            placeholder="авто" disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={2} title="Коефіцієнт">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Рік дії *</label>
          <input type="number" value={form.year}
            onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
            disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label title="Частка використання в оподатковуваних операціях (0..1), напр. 0.85 = 85%">
            Коефіцієнт (ЧВ) *</label>
          <input type="number" step="0.0001" min="0" max="1" value={form.coefficient}
            onChange={(e) => setForm({ ...form, coefficient: e.target.value })}
            placeholder="напр. 0.85" disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>На основі року *</label>
          <input type="number" value={form.based_on_year}
            onChange={(e) => setForm({ ...form, based_on_year: Number(e.target.value) })}
            disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата затвердження</label>
          <DatePopover value={form.approval_date}
            onChange={(v) => setForm({ ...form, approval_date: v })}
            disabled={isPosted} />
        </div>
      </div>

      {form.coefficient && (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>
          Оподатковувана частка: <b>{(Number(form.coefficient) * 100).toFixed(2)}%</b>;
          на необлагувану частку ({(100 - Number(form.coefficient) * 100).toFixed(2)}%)
          щомісяця складається зведена ПН за ст. 199.1 («Повʼязані дії» на проведеному документі).
        </p>
      )}
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={isPosted} />
        </div>
        <div>
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.proportional_vat_coefficient" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>

      {error && <div className="error">{error}</div>}

      {pnOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setPnOpen(false)}>
          <div className="card" style={{ width: 460, margin: 0 }}
            onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Зведена ПН за ст. 199.1</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setPnOpen(false)}>закрити</button>
            </div>
            <div className="doc-grid">
              <div className="field" style={{ gridColumn: 'span 6' }}>
                <label>Місяць</label>
                <select value={pnMonth} onChange={(e) => setPnMonth(Number(e.target.value))}>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ gridColumn: 'span 6' }}>
                <label>Рік</label>
                <input type="number" value={pnYear}
                  onChange={(e) => setPnYear(Number(e.target.value))} />
              </div>
              <div className="field" style={{ gridColumn: 'span 12' }}>
                <label title="Придбання подвійного призначення за місяць БЕЗ ПДВ — база для нарахування ПЗ на необлагувану частку">
                  База придбань за місяць (без ПДВ) *</label>
                <input type="number" step="0.01" value={pnBase}
                  onChange={(e) => setPnBase(e.target.value)}
                  style={{ textAlign: 'right' }} />
              </div>
              <div className="field" style={{ gridColumn: 'span 12' }}>
                <label>Тип причини (не видається покупцю)</label>
                <select value={pnReason} onChange={(e) => setPnReason(e.target.value)}>
                  <option value="09">09 — звільнені від ПДВ операції</option>
                  <option value="08">08 — не є обʼєктом оподаткування</option>
                  <option value="04">04 — неплатнику (негосподарське)</option>
                  <option value="13">13 — негосподарська діяльність</option>
                </select>
              </div>
            </div>
            {form.coefficient && pnBase && (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                ПЗ = {pnBase} × {(100 - Number(form.coefficient) * 100).toFixed(2)}% × 20% ={' '}
                <b>{fmt2(Number(pnBase) * (1 - Number(form.coefficient)) * 0.2)} грн</b>
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button onClick={() => setPnOpen(false)}>скасувати</button>
              <button className="primary" disabled={!pnBase || pnBusy}
                onClick={async () => {
                  setPnBusy(true); setError(null);
                  try {
                    const res = await propCoef.createCompiledPn(id!, {
                      month: pnMonth, year: pnYear,
                      base_amount: pnBase, not_issued_reason: pnReason,
                    });
                    setPnResult(`Створено зведену ПН №${res.invoice_number} (ПДВ ${fmt2(res.vat_amount)} грн) — підпис і ЄРПН у «ПН/РК (вихідні)»`);
                    setPnOpen(false);
                  } catch (e) {
                    setError(extractError(e));
                    setPnOpen(false);
                  } finally {
                    setPnBusy(false);
                  }
                }}>
                {pnBusy ? 'створення…' : 'створити'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========================= Річний перерахунок =========================

export function PropRecalcEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);
  const [calc, setCalc] = useState<PropRecalcCalculateResponse | null>(null);
  const y = new Date().getFullYear();
  const [form, setForm] = useState({
    organization_id: '', document_number: '', document_date: nowLocal(),
    year: y - 1, recalculation_date: `${y}-01-10`,
    total_taxable_supplies: '0', total_exempt_supplies: '0',
    coefficient_used_during_year: '', total_general_vat_credit: '0',
    comment: '',
  });

  const docQ = useQuery({
    queryKey: ['prop-recalc-doc', id],
    queryFn: () => propRecalc.get(id!),
    enabled: !isNew,
  });
  useEffect(() => {
    const d = docQ.data;
    if (!d) return;
    setForm({
      organization_id: d.organization_id,
      document_number: d.document_number,
      document_date: d.document_date.slice(0, 16),
      year: d.year,
      recalculation_date: d.recalculation_date,
      total_taxable_supplies: d.total_taxable_supplies,
      total_exempt_supplies: d.total_exempt_supplies,
      coefficient_used_during_year: d.coefficient_used_during_year,
      total_general_vat_credit: d.total_general_vat_credit,
      comment: d.comment ?? '',
    });
  }, [docQ.data]);
  const isPosted = !isNew && (docQ.data?.posted ?? false);

  const payload = () => ({
    organization_id: form.organization_id,
    document_number: form.document_number || null,
    document_date: form.document_date,
    year: Number(form.year),
    recalculation_date: form.recalculation_date,
    total_taxable_supplies: form.total_taxable_supplies || '0',
    total_exempt_supplies: form.total_exempt_supplies || '0',
    coefficient_used_during_year: form.coefficient_used_during_year,
    total_general_vat_credit: form.total_general_vat_credit || '0',
    comment: form.comment || null,
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['prop-recalc'] });
    qc.invalidateQueries({ queryKey: ['prop-recalc-doc', id] });
  };
  const create = useMutation({
    mutationFn: () => propRecalc.create(payload()),
    onSuccess: (d) => { invalidate(); navigate(`/taxation/proportional-vat-recalculation/${d.id}`); },
    onError: (e) => setError(extractError(e)),
  });
  const updPayload = () => ({
    document_date: form.document_date,
    year: Number(form.year),
    recalculation_date: form.recalculation_date,
    total_taxable_supplies: form.total_taxable_supplies || '0',
    total_exempt_supplies: form.total_exempt_supplies || '0',
    coefficient_used_during_year: form.coefficient_used_during_year,
    total_general_vat_credit: form.total_general_vat_credit || '0',
    comment: form.comment || null,
  });
  const update = useMutation({
    mutationFn: () => propRecalc.update(id!, updPayload()),
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    mutationFn: async () => { await propRecalc.update(id!, updPayload()); return propRecalc.post(id!); },
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => propRecalc.unpost(id!),
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => propRecalc.remove(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['prop-recalc'] }); navigate('/taxation/proportional-vat-recalculation'); },
    onError: (e) => setError(extractError(e)),
  });

  // Етап 2б: РК до зведеної ПН року.
  const [rkOpen, setRkOpen] = useState(false);
  const [rkBusy, setRkBusy] = useState(false);
  const [rkResult, setRkResult] = useState<string | null>(null);
  const compiledQ = useQuery({
    queryKey: ['recalc-compiled-pns', id],
    queryFn: () => propRecalc.compiledPns(id!),
    enabled: rkOpen && !isNew,
  });

  const taxable = Number(form.total_taxable_supplies || 0);
  const exempt = Number(form.total_exempt_supplies || 0);
  const actual = taxable + exempt > 0 ? taxable / (taxable + exempt) : 0;
  const used = Number(form.coefficient_used_during_year || 0);
  const credit = Number(form.total_general_vat_credit || 0);
  const diff = credit * (actual - used);

  async function calculateFromYear() {
    if (!form.organization_id) { setError('Оберіть організацію'); return; }
    setError(null);
    try {
      const res = await propRecalc.calculateFromYear(form.organization_id, Number(form.year));
      setCalc(res);
      if (!res.requires_manual_split) {
        setForm((f) => ({
          ...f,
          total_taxable_supplies: String(Number(res.taxable_supplies) + Number(res.export_supplies)),
          total_exempt_supplies: String(
            Number(res.exempt_supplies) + Number(res.non_business_supplies)
            + Number(res.not_object_supplies)),
        }));
      }
    } catch (e) {
      setError(extractError(e));
    }
  }

  // ч.889 (новий дизайн, Закон #10): «створити РК до зведеної ПН» — з шапки
  // у «Повʼязані дії» (disabled до проведення); «розрахувати з продажів року»
  // — у заголовку блоку 2.
  const railActions = [{
    // ч.1347: РК — зі збереженого перерахунку, проведення не чекаємо.
    label: 'створити РК до зведеної ПН',
    title: isNew ? 'Спершу збережіть документ'
      : 'РК до зведеної ПН року на суму перерахунку (ст. 199.4-199.5). Далі — підпис і реєстрація в ЄРПН.',
    disabled: isNew,
    onClick: () => { setRkResult(null); setRkOpen(true); },
  }];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'calc', title: 'Перерахунок', required: 6,
      filled: (form.year ? 1 : 0) + (form.recalculation_date ? 1 : 0)
        + (form.total_taxable_supplies ? 1 : 0) + (form.total_exempt_supplies ? 1 : 0)
        + (form.coefficient_used_during_year ? 1 : 0) + (form.total_general_vat_credit ? 1 : 0) },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          Річний перерахунок пропорційного ПДВ (ст. 199.4) {!isNew && `№ ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/taxation/proportional-vat-recalculation')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          savePending={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => post.mutate()}
          postPending={post.isPending}
          postDisabled={isNew}
          postTitle={isNew ? 'Спершу збережіть' : 'Зберігає та проводить'}
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={async () => {
            if (await confirm('Видалити документ?')) remove.mutate();
          }}
          entriesDoc={{ type: 'doc.proportional_vat_recalculation', id: isNew ? '' : id! }}
        />
        </div>
      </div>
      {rkResult && (
        <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.85rem' }}>{rkResult}</div>
      )}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.proportional_vat_recalculation', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <OrgSelect value={form.organization_id}
            onChange={(v) => setForm((f) => ({ ...f, organization_id: v }))}
            disabled={!isNew} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа</label>
          <input value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            placeholder="авто" disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={2} title="Перерахунок"
        actions={!isPosted ? (
          <button type="button" className="btn-doc" onClick={() => void calculateFromYear()}
            title="Підтягнути обсяги постачань з проведених реалізацій року (за податковим призначенням рядків)">
            <DocIcon />розрахувати з продажів року
          </button>
        ) : undefined}>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Рік перерахунку *</label>
          <input type="number" value={form.year}
            onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
            disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата перерахунку *</label>
          <DatePopover value={form.recalculation_date}
            onChange={(v) => setForm({ ...form, recalculation_date: v })}
            disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label title="Обсяг оподатковуваних постачань за рік (без ПДВ)">
            Оподатковувані постачання *</label>
          <input type="number" step="0.01" value={form.total_taxable_supplies}
            onChange={(e) => setForm({ ...form, total_taxable_supplies: e.target.value })}
            disabled={isPosted} style={{ textAlign: 'right' }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label title="Обсяг звільнених/необ'єктних постачань за рік">
            Звільнені постачання *</label>
          <input type="number" step="0.01" value={form.total_exempt_supplies}
            onChange={(e) => setForm({ ...form, total_exempt_supplies: e.target.value })}
            disabled={isPosted} style={{ textAlign: 'right' }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label title="Коефіцієнт, який застосовувався протягом року (з документа «Коефіцієнт»)">
            Використаний коефіцієнт *</label>
          <input type="number" step="0.0001" min="0" max="1"
            value={form.coefficient_used_during_year}
            onChange={(e) => setForm({ ...form, coefficient_used_during_year: e.target.value })}
            placeholder="напр. 0.85" disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label title="Загальний ПДВ-кредит за придбаннями подвійного призначення за рік">
            ПДВ-кредит за рік *</label>
          <input type="number" step="0.01" value={form.total_general_vat_credit}
            onChange={(e) => setForm({ ...form, total_general_vat_credit: e.target.value })}
            disabled={isPosted} style={{ textAlign: 'right' }} />
        </div>
      </div>

      {calc && (
        <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)', borderRadius: 6,
          padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: 'var(--badge-blue-fg)', marginTop: '0.5rem' }}>
          Продажів за {form.year}: {calc.posted_sales_count} на {fmt2(calc.total_supplies)} грн.
          {' '}{calc.suggestion}
        </div>
      )}

      <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0.75rem 0 0' }}>
        Фактичний коефіцієнт: <b>{actual ? actual.toFixed(4) : '—'}</b>
        {used > 0 && actual > 0 && (
          <>
            {' · '}різниця з використаним: <b>{(actual - used).toFixed(4)}</b>
            {' · '}коригування кредиту: <b style={{ color: diff >= 0 ? '#15803d' : '#dc2626' }}>
              {fmt2(diff)} грн</b>
            {' '}({diff >= 0 ? 'донарахування кредиту' : 'зменшення кредиту'})
          </>
        )}
      </p>
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={isPosted} />
        </div>
        <div>
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.proportional_vat_recalculation" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>

      {error && <div className="error">{error}</div>}

      {rkOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setRkOpen(false)}>
          <div className="card" style={{ width: 560, maxHeight: '70vh', overflowY: 'auto', margin: 0 }}
            onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Оберіть зведену ПН {form.year} року</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setRkOpen(false)}>закрити</button>
            </div>
            {compiledQ.isLoading && <p style={{ color: 'var(--muted)' }}>Завантаження…</p>}
            {compiledQ.data && compiledQ.data.items.length === 0 && (
              <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
                Зведених ПН за {form.year} рік немає — спершу створіть їх з
                документа «Коефіцієнт» (кнопка «створити зведену ПН»).
              </p>
            )}
            {(compiledQ.data?.items ?? []).map((pn) => (
              <div key={pn.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                borderTop: '1px solid var(--border)', padding: '0.4rem 0' }}>
                <span style={{ flex: 1 }}>
                  ПН №{pn.invoice_number} від {new Date(pn.invoice_date).toLocaleDateString('uk-UA')}
                  {' · '}{fmt2(pn.total_amount)} грн
                </span>
                <button disabled={rkBusy}
                  onClick={async () => {
                    setRkBusy(true); setError(null);
                    try {
                      const res = await propRecalc.createRk(id!, pn.id);
                      setRkResult(`Створено РК №${res.invoice_number} (ΔПДВ ${fmt2(res.vat_delta)} грн) — підпис і ЄРПН у «ПН/РК (вихідні)»`);
                      setRkOpen(false);
                    } catch (e) {
                      setError(extractError(e));
                      setRkOpen(false);
                    } finally {
                      setRkBusy(false);
                    }
                  }}>обрати</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ===================== Коригування очікуваного ПДВ =====================

const ADJ_TYPES: Array<[AdjustmentType, string]> = [
  ['REGISTERED_WITH_DIFFERENCE', 'ПН зареєстрована з різницею'],
  ['NOT_REGISTERED', 'ПН не зареєстрована'],
  ['CUSTOMS_RECALC', 'Митниця перерахувала'],
  ['OTHER', 'Інше (аудит)'],
];

export function EVCVAEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    organization_id: '', document_number: '', document_date: nowLocal(),
    adjustment_type: 'REGISTERED_WITH_DIFFERENCE' as AdjustmentType,
    // ч.1339: вхідна ПН, кредит за якою коригується (без неї документ не зберігався).
    original_tax_invoice_incoming_id: '',
    expected_amount: '', confirmed_amount: '0',
    adjustment_reason: '', comment: '',
  });

  const docQ = useQuery({
    queryKey: ['evcva-doc', id],
    queryFn: () => evcva.get(id!),
    enabled: !isNew,
  });
  useEffect(() => {
    const d = docQ.data;
    if (!d) return;
    setForm({
      organization_id: d.organization_id,
      document_number: d.document_number,
      document_date: d.document_date.slice(0, 16),
      adjustment_type: d.adjustment_type,
      original_tax_invoice_incoming_id: d.original_tax_invoice_incoming_id ?? '',
      expected_amount: d.expected_amount,
      confirmed_amount: d.confirmed_amount,
      adjustment_reason: (d as { adjustment_reason?: string }).adjustment_reason ?? '',
      comment: d.comment ?? '',
    });
  }, [docQ.data]);
  const isPosted = !isNew && (docQ.data?.posted ?? false);

  const invoicesQ = useQuery({
    queryKey: ['evcva-incoming-pn', form.organization_id],
    queryFn: () => taxInvoicesIncoming.list({
      organization_id: form.organization_id, limit: 500, sort_by: 'invoice_date', sort_dir: 'desc' }),
    enabled: !!form.organization_id,
  });
  const invoiceItems = (invoicesQ.data?.items ?? [])
    .filter((i) => !i.deletion_mark || i.id === form.original_tax_invoice_incoming_id)
    .map((i) => ({
      id: i.id,
      label: `${i.is_correction ? 'РК' : 'ПН'} № ${i.invoice_number} від ${new Date(i.invoice_date).toLocaleDateString('uk-UA')} — ${i.contractor_name}`,
      hint: `${fmt2(Number(i.total_amount))} грн`,
    }));
  // Очікувана сума за замовчуванням — ПДВ обраної накладної.
  const pickInvoice = async (invId: string) => {
    setForm((f) => ({ ...f, original_tax_invoice_incoming_id: invId }));
    if (!invId) return;
    try {
      const inv = await taxInvoicesIncoming.get(invId);
      setForm((f) => (f.original_tax_invoice_incoming_id === invId && !f.expected_amount
        ? { ...f, expected_amount: (Number(inv.vat_20 || 0) + Number(inv.vat_7 || 0)
          + Number(inv.vat_14 || 0)).toFixed(2) } : f));
    } catch { /* сума лишається для ручного вводу */ }
  };

  const payload = () => ({
    organization_id: form.organization_id,
    document_number: form.document_number || null,
    document_date: form.document_date,
    adjustment_type: form.adjustment_type,
    original_tax_invoice_incoming_id: form.original_tax_invoice_incoming_id || null,
    expected_amount: form.expected_amount,
    confirmed_amount: form.confirmed_amount || '0',
    adjustment_reason: form.adjustment_reason,
    comment: form.comment || null,
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['evcva'] });
    qc.invalidateQueries({ queryKey: ['evcva-doc', id] });
  };
  const create = useMutation({
    mutationFn: () => evcva.create(payload()),
    onSuccess: (d) => { invalidate(); navigate(`/taxation/expected-confirmed-vat-adj/${d.id}`); },
    onError: (e) => setError(extractError(e)),
  });
  const updPayload = () => ({
    document_date: form.document_date,
    adjustment_type: form.adjustment_type,
    original_tax_invoice_incoming_id: form.original_tax_invoice_incoming_id || null,
    expected_amount: form.expected_amount,
    confirmed_amount: form.confirmed_amount || '0',
    adjustment_reason: form.adjustment_reason,
    comment: form.comment || null,
  });
  const update = useMutation({
    mutationFn: () => evcva.update(id!, updPayload()),
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    mutationFn: async () => { await evcva.update(id!, updPayload()); return evcva.post(id!); },
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => evcva.unpost(id!),
    onSuccess: invalidate,
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => evcva.remove(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['evcva'] }); navigate('/taxation/expected-confirmed-vat-adj'); },
    onError: (e) => setError(extractError(e)),
  });

  const diff = Number(form.confirmed_amount || 0) - Number(form.expected_amount || 0);

  // ч.889 (новий дизайн, Закон #10): «ще дій» не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 3,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) + (form.adjustment_type ? 1 : 0) },
    { id: 'sums', title: 'Суми і причина', required: 3,
      filled: (form.original_tax_invoice_incoming_id ? 1 : 0) + (form.expected_amount ? 1 : 0)
        + (form.adjustment_reason.length >= 3 ? 1 : 0) },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          Коригування очікуваного/підтвердженого ПДВ {!isNew && `№ ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/taxation/expected-confirmed-vat-adj')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          savePending={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => post.mutate()}
          postPending={post.isPending}
          postDisabled={isNew}
          postTitle={isNew ? 'Спершу збережіть' : 'Зберігає та проводить'}
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={async () => {
            if (await confirm('Видалити документ?')) remove.mutate();
          }}
          entriesDoc={{ type: 'doc.expected_vs_confirmed_vat_adjustments', id: isNew ? '' : id! }}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.expected_vs_confirmed_vat_adjustments', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Організація *</label>
          <OrgSelect value={form.organization_id}
            onChange={(v) => setForm((f) => ({ ...f, organization_id: v }))}
            disabled={!isNew} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>№ документа</label>
          <input value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
            placeholder="авто" disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Тип коригування *</label>
          <select value={form.adjustment_type}
            onChange={(e) => setForm({ ...form, adjustment_type: e.target.value as AdjustmentType })}
            disabled={isPosted}>
            {ADJ_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      </DocBlock>

      <DocBlock index={2} title="Суми і причина">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <label>Вхідна податкова накладна *</label>
          <SearchSelect items={invoiceItems} value={form.original_tax_invoice_incoming_id}
            onChange={(v) => { void pickInvoice(v); }} disabled={isPosted || !form.organization_id}
            placeholder={!form.organization_id ? 'спершу оберіть організацію'
              : invoicesQ.isLoading ? 'завантаження…'
              : invoiceItems.length ? '— номер ПН або постачальник —'
              : 'у цій організації немає вхідних податкових накладних'} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Очікувана сума ПДВ *</label>
          <input type="number" step="0.01" value={form.expected_amount}
            onChange={(e) => setForm({ ...form, expected_amount: e.target.value })}
            disabled={isPosted} style={{ textAlign: 'right' }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Підтверджена сума ПДВ</label>
          <input type="number" step="0.01" value={form.confirmed_amount}
            onChange={(e) => setForm({ ...form, confirmed_amount: e.target.value })}
            disabled={isPosted} style={{ textAlign: 'right' }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Причина коригування *</label>
          <input value={form.adjustment_reason}
            onChange={(e) => setForm({ ...form, adjustment_reason: e.target.value })}
            placeholder="мін. 3 символи — напр. «ПН №… зареєстрована на іншу суму»"
            disabled={isPosted} />
        </div>
      </div>

      <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0.5rem 0 0' }}>
        Різниця (підтверджено − очікувано): <b style={{ color: diff >= 0 ? '#15803d' : '#dc2626' }}>
          {fmt2(diff)} грн</b>
      </p>
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={isPosted} />
        </div>
        <div>
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.expected_vs_confirmed_vat_adjustments" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}
    </div>
  );
}
